import React, { useRef } from 'react';
import { Check, Minus } from 'lucide-react';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  description?: string;
  error?: string;
  indeterminate?: boolean;
  checkboxSize?: 'sm' | 'md' | 'lg';
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      indeterminate = false,
      checkboxSize = 'md',
      className = '',
      id,
      disabled = false,
      checked,
      onChange,
      ...props
    },
    forwardedRef
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const ref = (forwardedRef as React.RefObject<HTMLInputElement>) || internalRef;

    const checkboxId = id || `checkbox-${Math.random().toString(36).substring(2, 9)}`;
    const hasError = !!error;

    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    const iconSizes = {
      sm: 12,
      md: 14,
      lg: 18,
    };

    const baseClasses =
      'rounded border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

    const stateClasses = hasError
      ? 'border-red-500 focus:ring-red-200'
      : checked || indeterminate
        ? 'border-primary bg-primary focus:ring-primary/20'
        : 'border-gray-300 bg-white hover:border-primary/50 focus:ring-primary/20';

    const disabledClasses = disabled
      ? 'opacity-50 cursor-not-allowed'
      : 'cursor-pointer';

    const checkboxClasses = `${baseClasses} ${sizeClasses[checkboxSize]} ${stateClasses} ${disabledClasses}`;

    const handleClick = () => {
      if (disabled) return;
      // Déclencher le clic sur l'input caché
      if (ref && 'current' in ref && ref.current) {
        ref.current.click();
      }
    };

    return (
      <div className={className}>
        <div className="flex items-start gap-3">
          <div className="relative flex items-center">
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              className="sr-only peer"
              disabled={disabled}
              checked={checked}
              onChange={onChange}
              aria-invalid={hasError}
              aria-describedby={error ? `${checkboxId}-error` : undefined}
              {...props}
            />
            <div
              className={checkboxClasses}
              onClick={handleClick}
              role="checkbox"
              aria-checked={checked}
              tabIndex={disabled ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  handleClick();
                }
              }}
            >
              {(checked || indeterminate) && (
                <div className="absolute inset-0 flex items-center justify-center text-white">
                  {indeterminate ? (
                    <Minus size={iconSizes[checkboxSize]} strokeWidth={3} />
                  ) : (
                    <Check size={iconSizes[checkboxSize]} strokeWidth={3} />
                  )}
                </div>
              )}
            </div>
          </div>

          {(label || description) && (
            <label
              htmlFor={checkboxId}
              className={`flex-1 select-none ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            >
              {label && (
                <span className="block text-sm font-medium text-gray-900">
                  {label}
                </span>
              )}
              {description && (
                <span className="block text-sm text-gray-600 mt-0.5">
                  {description}
                </span>
              )}
            </label>
          )}
        </div>

        {error && (
          <p
            id={`${checkboxId}-error`}
            className="mt-1 text-sm text-red-600 ml-8"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
